import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import { AlertTriangle, Home02, RefreshCcw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = error instanceof Error ? error.message : String(error);

  return (
    <div className="grid min-h-[70vh] place-items-center px-6 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-xl bg-primary p-8 text-center ring-1 ring-secondary">
        <div className="grid size-12 place-items-center rounded-xl bg-utility-error-50 text-utility-error-700">
          <AlertTriangle className="size-6" />
        </div>
        <div className="flex flex-col items-center gap-1">
          <h2 className="text-lg font-semibold text-primary">{t("error.title")}</h2>
          <p className="max-w-sm text-sm text-tertiary">{t("error.description")}</p>
        </div>
        <details className="w-full">
          <summary className="cursor-pointer text-center text-xs font-semibold text-quaternary">{t("error.technical")}</summary>
          <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-secondary p-3 text-left font-mono text-xs text-secondary">{message}</pre>
        </details>
        <div className="flex flex-wrap justify-center gap-2">
          <Button
            color="secondary"
            size="md"
            iconLeading={Home02}
            onPress={() => {
              resetErrorBoundary();
              navigate("/", { replace: true });
            }}
          >
            {t("error.goHome")}
          </Button>
          <Button color="primary" size="md" iconLeading={RefreshCcw01} onPress={resetErrorBoundary}>
            {t("error.tryAgain")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Wraps a route subtree with an error boundary that resets automatically when
 * the URL changes, so navigating away from a broken page recovers.
 */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary FallbackComponent={Fallback} resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  );
}
